'use client';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Plus } from 'lucide-react';
import type { IntervalLine } from '../../types';

interface IntervalDialogProps {
  open: boolean;
  onClose: () => void;
  intervalLines: IntervalLine[];
  onAddIntervalLines: (lines: IntervalLine[]) => void;
  onRemoveIntervalLine: (id: string) => void;
  xAxisData?: any[];
  xAxisType?: 'date' | 'numeric' | 'category';
  variant?: 'harmonics' | 'carrier';
}

/**
 * Generic Interval Dialog for DataLayers
 * Works with any data layer type that needs interval markers
 */
export const IntervalDialog: React.FC<IntervalDialogProps> = ({
  open,
  onClose,
  intervalLines,
  onAddIntervalLines,
  onRemoveIntervalLine,
  xAxisData = [],
  xAxisType = 'date',
  variant = 'harmonics',
}) => {
  const [startingPoint, setStartingPoint] = useState('');
  const [intervalValue, setIntervalValue] = useState('');
  const [intervalUnit, setIntervalUnit] = useState('minutes');
  const [numberOfLines, setNumberOfLines] = useState('5');
  const [label, setLabel] = useState('Interval');
  const [selectedColor, setSelectedColor] = useState('#ff4444');

  const colors = [
    { name: 'Red', value: '#ff0000' },
    { name: 'Green', value: '#00cc00' },
    { name: 'Blue', value: '#0066ff' },
    { name: 'Magenta', value: '#ff00ff' },
    { name: 'Cyan', value: '#00ffff' },
    { name: 'Orange', value: '#ff6600' },
    { name: 'Yellow', value: '#ffcc00' },
    { name: 'Gray', value: '#999999' },
  ];

  useEffect(() => {
    if (open && xAxisData.length > 0) {
      const firstValue = xAxisData[0];
      if (xAxisType === 'date') {
        setStartingPoint(new Date(firstValue).toISOString().slice(0, 16));
      } else {
        setStartingPoint(String(firstValue));
      }
    }
  }, [open, xAxisData, xAxisType]);

  const generateIntervalLines = () => {
    const lines: IntervalLine[] = [];
    const numLines = parseInt(numberOfLines) || 0;
    const interval = parseFloat(intervalValue) || 0;

    if (numLines <= 0 || interval <= 0) return;

    let baseValue: Date | number;
    if (xAxisType === 'date') {
      baseValue = new Date(startingPoint);
    } else {
      baseValue = parseFloat(startingPoint) || 0;
    }

    // Calculate interval in milliseconds (for date type)
    let intervalMs = interval;
    if (xAxisType === 'date') {
      switch (intervalUnit) {
        case 'seconds':
          intervalMs *= 1000;
          break;
        case 'minutes':
          intervalMs *= 60 * 1000;
          break;
        case 'hours':
          intervalMs *= 60 * 60 * 1000;
          break;
        case 'days':
          intervalMs *= 24 * 60 * 60 * 1000;
          break;
        case 'weeks':
          intervalMs *= 7 * 24 * 60 * 60 * 1000;
          break;
      }
    }

    // Generate lines based on variant
    if (variant === 'harmonics') {
      // Harmonics: Generate lines to the right only (starting from base point)
      for (let i = 0; i < numLines; i++) {
        let startTime: Date | number;
        let endTime: Date | number;

        if (xAxisType === 'date') {
          const baseDate = new Date(baseValue);
          startTime = new Date(baseDate.getTime() + i * intervalMs);
          endTime = new Date(startTime.getTime() + intervalMs);
        } else {
          startTime = (baseValue as number) + i * interval;
          endTime = startTime + interval;
        }

        lines.push({
          id: `interval-${Date.now()}-${i}`,
          startTime,
          endTime,
          label: `${label} ${i + 1}`,
          color: selectedColor,
        });
      }
    } else if (variant === 'carrier') {
      // Carrier: Generate lines in both directions (left and right from base point)
      // Generate left side (negative indices)
      for (let i = -numLines; i < 0; i++) {
        let startTime: Date | number;
        let endTime: Date | number;

        if (xAxisType === 'date') {
          const baseDate = new Date(baseValue);
          startTime = new Date(baseDate.getTime() + i * intervalMs);
          endTime = new Date(startTime.getTime() + intervalMs);
        } else {
          startTime = (baseValue as number) + i * interval;
          endTime = startTime + interval;
        }

        lines.push({
          id: `interval-${Date.now()}-left-${Math.abs(i)}`,
          startTime,
          endTime,
          label: `${label} ${i}`,
          color: selectedColor,
        });
      }

      // Generate center line (base point)
      if (xAxisType === 'date') {
        const baseDate = new Date(baseValue);
        lines.push({
          id: `interval-${Date.now()}-center`,
          startTime: baseDate,
          endTime: new Date(baseDate.getTime() + intervalMs),
          label: `${label} 0`,
          color: selectedColor,
        });
      } else {
        lines.push({
          id: `interval-${Date.now()}-center`,
          startTime: baseValue as number,
          endTime: (baseValue as number) + interval,
          label: `${label} 0`,
          color: selectedColor,
        });
      }

      // Generate right side (positive indices)
      for (let i = 1; i <= numLines; i++) {
        let startTime: Date | number;
        let endTime: Date | number;

        if (xAxisType === 'date') {
          const baseDate = new Date(baseValue);
          startTime = new Date(baseDate.getTime() + i * intervalMs);
          endTime = new Date(startTime.getTime() + intervalMs);
        } else {
          startTime = (baseValue as number) + i * interval;
          endTime = startTime + interval;
        }

        lines.push({
          id: `interval-${Date.now()}-right-${i}`,
          startTime,
          endTime,
          label: `${label} +${i}`,
          color: selectedColor,
        });
      }
    }

    onAddIntervalLines(lines);
    onClose();
  };

  const timeUnits = ['seconds', 'minutes', 'hours', 'days', 'weeks'];
  const numericUnits = ['units', 'tens', 'hundreds', 'thousands'];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Manage Interval Lines - {variant === 'harmonics' ? 'Harmonics' : 'Carrier Frequency'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Configuration section */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="starting-point">Starting Point</Label>
                <Input
                  id="starting-point"
                  type={xAxisType === 'date' ? 'datetime-local' : 'number'}
                  value={startingPoint}
                  onChange={(e) => setStartingPoint(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="interval-value">Interval Value</Label>
                <div className="flex gap-2">
                  <Input
                    id="interval-value"
                    type="number"
                    value={intervalValue}
                    onChange={(e) => setIntervalValue(e.target.value)}
                    placeholder="e.g., 30"
                  />
                  <Select value={intervalUnit} onValueChange={setIntervalUnit}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(xAxisType === 'date' ? timeUnits : numericUnits).map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {unit}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="number-of-lines">Number of Lines</Label>
                <Input
                  id="number-of-lines"
                  type="number"
                  value={numberOfLines}
                  onChange={(e) => setNumberOfLines(e.target.value)}
                  min="1"
                  max="20"
                />
              </div>

              <div>
                <Label htmlFor="label">Label Prefix</Label>
                <Input
                  id="label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g., Interval"
                />
              </div>

              <div>
                <Label htmlFor="color">Color</Label>
                <Select value={selectedColor} onValueChange={setSelectedColor}>
                  <SelectTrigger>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: selectedColor }}
                      />
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {colors.map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: color.value }}
                          />
                          {color.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button onClick={generateIntervalLines} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Generate Interval Lines
            </Button>
          </div>

          {/* Existing interval lines */}
          {intervalLines.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Existing Interval Lines</h3>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {intervalLines.map((line) => (
                  <div
                    key={line.id}
                    className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: line.color || '#999' }}
                      />
                      <span className="text-sm">{line.label}</span>
                      <span className="text-xs text-gray-500">
                        {xAxisType === 'date'
                          ? `${new Date(line.startTime).toLocaleString()} - ${new Date(line.endTime).toLocaleString()}`
                          : `${line.startTime} - ${line.endTime}`}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onRemoveIntervalLine(line.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};